import React, { useEffect, useState, useCallback, useRef } from "react";
import Logo from "../../assets/logo-full.png";
import { Icon } from "@iconify/react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import InfiniteScroll from "react-infinite-scroll-component";
import { industries } from "./admin/validationSchema";
import MultiRangeSlider from "./../../components/multiRangeSlider";
import debounce from "lodash/debounce";
import Layout from "../Layout";
import { Button } from "@chakra-ui/react";
import ProjectCard from "./ProjectCard";
import useRole from "../../utils/useRole";
import usePageSnapshot from "../../hooks/usePageSnapshot";
import MyToggle from "../../components/MyToggle";

const ProjectListing = () => {
  const navigate = useNavigate();
  // Coming back from a project restores the loaded pages, filters and scroll.
  const { restored, remember } = usePageSnapshot("project-showcase-list");
  const [projectData, setProjectData] = useState(restored?.projectData ?? []);
  const [currentPage, setCurrentPage] = useState(restored?.currentPage ?? 1);
  const [hasMore, setHasMore] = useState(restored?.hasMore ?? true);
  const [filterMenu, setFilterMenu] = useState(false);
  const [searchQuery, setSeearchQuery] = useState(restored?.searchQuery ?? "");
  const [selectedIndustry, setSelectedIndustry] = useState(
    restored?.selectedIndustry ?? "",
  );
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedSpaceSize, setSelectedSpaceSize] = useState(
    restored?.selectedSpaceSize ?? [0, 50000],
  );
  const [isFavorite, setIsFavorite] = useState(restored?.isFavorite ?? false);
  remember({
    projectData,
    currentPage,
    hasMore,
    searchQuery,
    selectedIndustry,
    selectedSpaceSize,
    isFavorite,
  });
  const filterRef = useRef(null);
  const isMountedRef = useRef(false);
  const { isUser, onlyAdmins, isSuperAdmin } = useRole();
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updatedProjects = [...projectData];
    const [draggedItem] = updatedProjects.splice(draggedIndex, 1);
    updatedProjects.splice(index, 0, draggedItem);

    setProjectData(updatedProjects);
    setDraggedIndex(index);
  };

  const handleDrop = async () => {
    setDraggedIndex(null);

    try {
      const ids = projectData.map((item) => item._id);
      await axios.patch(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases/update-order`,
        { ids },
        axiosConfig,
      );
    } catch (error) {
      console.error("Error updating project order:", error);
    }
  };

  // const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    industry: "",
    // spaceSize: [0, 50000],
    search: "",
  });

  const token = localStorage.getItem("usersdatatoken");
  const axiosConfig = {
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  };

  const fetchProjects = async (page = 1, filters = {}) => {
    try {
      // setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases`,
        {
          params: { page, limit: 30, ...filters },
        },
      );
      const newProjects = response.data.results.filter(
        (item) => item.isLive === true,
      );

      setProjectData((prevProjects) =>
        page === 1 ? newProjects : [...prevProjects, ...newProjects],
      );
      setCurrentPage(response.data.currentPage);
      if (currentPage >= response.data.totalPages) {
        setHasMore(false);
      }
    } catch (error) {
      console.error(
        "Error fetching projects:",
        error.response ? error.response.data : error,
      );
    }
  };

  const fetchMoreProjects = () => {
    const params = {
      search: searchQuery,
      industry: selectedIndustry,
    };
    if (isFavorite) {
      params.isFavorite = true;
    }
    if (selectedSpaceSize[0] !== 0 || selectedSpaceSize[1] !== 50000) {
      params.spaceSize = selectedSpaceSize;
    }
    fetchProjects(currentPage + 1, params);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleSearchChange = (e) => {
    // Update the search filter state
    setFilters({ ...filters, search: e.target.value });

    // Define a debounced fetch function
    const fetchSelfPlansDebounced = debounce(() => {
      // Call fetchProjects with the current filters after debounce
      fetchProjects(1, filters);
    }, 1000);

    // Call the debounced function
    fetchSelfPlansDebounced();

    // Return a cleanup function to cancel debounced calls on component unmount or re-render
    return () => {
      fetchSelfPlansDebounced.cancel();
    };
  };

  const handleFilterClear = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      industry: "",
      // spaceSize: [0, 50000],
      search: "",
    }));
    setProjectData([]);
    setCurrentPage(1);
    fetchProjects(1, {});
    setFilterMenu(false);
  };

  const handleSpaceSizeChange = (value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      spaceSize: value,
    }));
  };
  // Debounced fetch ref so it persists across renders
  const debouncedFetchRef = useRef(
    debounce((search, industry, spaceSize, isFav) => {
      setProjectData([]);
      setCurrentPage(1);
      setHasMore(true);
      const params = { search, industry };
      if (isFav) {
        params.isFavorite = true;
      }
      if (spaceSize && (spaceSize[0] !== 0 || spaceSize[1] !== 50000)) {
        params.spaceSize = spaceSize;
      }
      fetchProjects(1, params);
    }, 500),
  );

  // Trigger fetch whenever searchQuery, selectedIndustry, or selectedSpaceSize changes.
  // On first mount call immediately (no debounce); on subsequent changes, debounce.
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      if (restored) return;
      const params = {};
      if (isFavorite) params.isFavorite = true;
      if (searchQuery) params.search = searchQuery;
      if (selectedIndustry) params.industry = selectedIndustry;
      if (selectedSpaceSize[0] !== 0 || selectedSpaceSize[1] !== 50000) {
        params.spaceSize = selectedSpaceSize;
      }
      fetchProjects(1, params);
      return;
    }
    debouncedFetchRef.current(
      searchQuery,
      selectedIndustry,
      selectedSpaceSize,
      isFavorite,
    );
    return () => debouncedFetchRef.current.cancel();
  }, [searchQuery, selectedIndustry, selectedSpaceSize, isFavorite]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchInput = (value) => {
    setSeearchQuery(value);
  };

  const handleIndustrySelect = (industry) => {
    setSelectedIndustry((prev) => (prev === industry ? "" : industry));
  };

  const handleClearAllFilters = () => {
    setSelectedIndustry("");
    setSelectedSpaceSize([0, 50000]);
    setShowFilterDropdown(false);
  };

  const activeFilterCount =
    (selectedIndustry ? 1 : 0) +
    (selectedSpaceSize[0] !== 0 || selectedSpaceSize[1] !== 50000 ? 1 : 0);

  return (
    <div>
      {/* <aside
        id="logo-sidebar"
        className={`fixed top-0 left-0 z-50 w-80 h-screen pt-5 pb-14 transition-transform ${
          filterMenu ? "translate-x-0" : "-translate-x-full"
        } bg-white border-r border-gray-200`}
        aria-label="Sidebar"
      >
        <div className="flex justify-between px-3 items-center mb-5">
          <h2 className="text-2xl font-bold">Filter</h2>
          <div
            onClick={() => setFilterMenu(false)}
            className="bg-[#efefef] p-2 rounded-full"
          >
            <Icon icon="humbleicons:times" />
          </div>
        </div>

        <div className="h-full px-3 py-4 overflow-y-auto">
          <div className="mb-4">
            <label className="text-sm font-semibold block mb-2 tracking-wider">
              Industry :{" "}
            </label>

            <select
              name="industry"
              value={filters.industry}
              onChange={handleInputChange}
              className="py-2 px-3 border w-full rounded-lg outline-primary-500"
              placeholder="Enter industry here..."
            >
              <option value="" disabled>
                Select industry
              </option>
              {industries.map((industry, idx) => (
                <option key={idx} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </div>

          <div className="relative mb-10">
            <label className="text-sm font-semibold block mb-2 tracking-wider">
              Space-Size Range in (Sqft) :{" "}
            </label>
            <MultiRangeSlider
              min={0}
              max={50000}
              value={filters?.spaceSize}
              onChange={handleSpaceSizeChange}
            />
          </div>

          {(filters.spaceSize[0] !== 0 ||
            filters.spaceSize[1] !== 50000 ||
            filters.industry) && (
            <div className="flex gap-3 items-center mt-10 justify-end">
              <button
                className="mt-5 py-2 px-3 bg-white border-2 border-primary-500 text-primary-500 rounded-lg flex gap-2 items-center justify-center"
                onClick={() => {
                  handleFilterClear();
                }}
              >
                <Icon icon="humbleicons:times" /> Clear
              </button>
              <button
                className="mt-5 py-2 px-3 bg-primary-500 border-2 border-primary-500 text-white rounded-lg flex gap-2 items-center justify-center"
                onClick={() => {
                  fetchProjects(1, filters);
                  setFilterMenu(false);
                }}
              >
                <Icon icon="humbleicons:check" /> Apply
              </button>
            </div>
          )}
        </div>
      </aside>
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 shadow-md fixed w-full z-40 bg-white">
        <div className="grow md:grow-0">
          <img width={120} src={Logo} />
        </div>
        <div className="flex gap-3 w-full max-w-[600px]">
          <div className="border-2 rounded-lg overflow-hidden w-full relative">
            <input
              name="search"
              value={filters.search}
              onChange={handleSearchChange}
              placeholder="Search by Keywords..."
              className="w-full py-2 pl-8 pr-3 border-none outline-none"
            />
            <Icon
              icon={"ion:search"}
              className="absolute top-1/2 -translate-y-1/2 left-2 text-2xl text-gray-400"
            />
          </div>
          <button
            className="py-2 px-3 bg-primary-500 text-white rounded-lg flex gap-2 items-center justify-center"
            onClick={() => setFilterMenu(true)}
          >
            <Icon icon="octicon:filter-16" /> Filters
          </button>
        </div>
     </div> */}

      <Layout
        ProjectShowcase={true}
        OnChangeSearchInput={handleSearchInput}
        initialSearchValue={searchQuery}
      >
        <div className="px-3 mx-auto container project-list-public min-h-screen pt-6 md:pt-8">
          <div className="flex items-center justify-between mb-4">
            {/* Filter button - left side */}
            <div className="flex items-center gap-4">
              <div className="relative" ref={filterRef}>
                <Button
                  variant={"outline"}
                  onClick={() => setShowFilterDropdown((prev) => !prev)}
                  size={"sm"}
                  colorScheme="teal"
                  leftIcon={<Icon icon="octicon:filter-16" />}
                >
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-1.5 bg-teal-600 text-white text-xs w-5 h-5 rounded-full inline-flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>

                {/* Filter dropdown */}
                {showFilterDropdown && (
                  <div className="absolute left-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 p-4">
                    {/* Industry section */}
                    <div className="mb-4">
                      <label className="text-sm font-semibold block mb-2 text-gray-700">
                        Industry
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {industries.map((industry) => (
                          <button
                            key={industry}
                            onClick={() => {
                              handleIndustrySelect(industry);
                              setShowFilterDropdown(false);
                            }}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                              selectedIndustry === industry
                                ? "bg-teal-600 text-white border-teal-600"
                                : "bg-gray-50 text-gray-600 border-gray-200 hover:border-teal-400 hover:text-teal-600"
                            }`}
                          >
                            {industry}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Space size section */}
                    <div className="mb-4">
                      <label className="text-sm font-semibold block mb-2 text-gray-700">
                        Space Size (Sqft)
                      </label>
                      <div className="px-1 pt-2 pb-6">
                        <MultiRangeSlider
                          min={0}
                          max={50000}
                          value={selectedSpaceSize}
                          onChange={(val) => setSelectedSpaceSize(val)}
                        />
                      </div>
                    </div>

                    {/* Footer actions */}
                    {activeFilterCount > 0 && (
                      <div className="flex justify-end border-t border-gray-100 pt-3">
                        <button
                          onClick={handleClearAllFilters}
                          className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1 font-medium"
                        >
                          <Icon
                            icon="humbleicons:times"
                            className="text-base"
                          />
                          Clear All
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <MyToggle
                checked={isFavorite}
                onChange={(val) => setIsFavorite(val)}
              />
            </div>

            {/* Right side buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant={"outline"}
                onClick={() => navigate("all")}
                size={"sm"}
                colorScheme="teal"
              >
                All Projects
              </Button>
              {onlyAdmins ? (
                <Button
                  onClick={() => navigate("add")}
                  size={"sm"}
                  colorScheme="teal"
                >
                  Add Projects
                </Button>
              ) : null}
            </div>
          </div>
          {projectData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-16 h-16 mb-4 opacity-40"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 7h18M3 12h18M3 17h18"
                />
              </svg>
              <p className="text-lg font-semibold">No projects available</p>
              <p className="text-sm mt-1">Try adjusting your filters.</p>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={projectData.length}
              next={fetchMoreProjects}
              hasMore={hasMore}
              loader={
                <div className="text-center text-primary-500 text-xl ">
                  Loading ...
                </div>
                //             <div className="flex gap-2 gap-y-6 justify-between flex-wrap">
                // {
                //             [...Array(4)].map((_) => (
                //             <div className="text-center w-[350px] h-80 bg-gray-300 animate-pulse rounded-xl"></div>
                //           ))}
                //             </div>
              }
            >
              <ResponsiveMasonry
                columnsCountBreakPoints={{ 350: 1, 600: 2, 950: 3 }}
              >
                <Masonry gutter={"0.5rem"}>
                  {projectData.map((item, index) => {
                    const isDragging = draggedIndex === index;
                    return (
                      <div
                        key={item._id}
                        draggable={isSuperAdmin}
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={handleDrop}
                        className={`transition-all duration-500 ease-out transform-gpu origin-center ${
                          isSuperAdmin
                            ? "cursor-grab active:cursor-grabbing hover:shadow-[0_20px_35px_rgba(0,0,0,0.2)] hover:-translate-y-1.5"
                            : ""
                        } ${
                          isDragging
                            ? "opacity-15 scale-[0.85] shadow-none"
                            : ""
                        }`}
                      >
                        <ProjectCard
                          item={item}
                          allProjects={projectData}
                          setProjectData={setProjectData}
                          // refetchProject={}
                        />
                      </div>
                    );
                  })}
                </Masonry>
              </ResponsiveMasonry>
            </InfiniteScroll>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default ProjectListing;
